/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import '../styles/main.scss';

import { graphql } from 'gatsby';
import groq from 'groq';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ModulesContent, { CommonModuleProps, Module } from '../components/ModulesContent';
import PageSEO from '../components/PageSEO';
import Hero, { HeroType } from '../components/ui/Hero';
import HomepageHero from '../components/ui/HomepageHero';
import SanityLayout from '../components/ui/SanityLayout';
import { ThemeModuleProps } from '../components/ui/modules/ThemeModule';
import { ALL_LANGS } from '../constants';
import { LocalizedSEO } from '../graphql-fragments/seo';
import { withPagePreview } from '../preview/previewUtils';
import { withI18n } from '../utils/hocs';
import { useIntersectionObserver } from '../utils/hooks';
import { localize } from '../utils/sanity';

export const query = graphql`
  query GetPageById($id: String!) {
    sanityPage(_id: { eq: $id }) {
      title {
        ...LocaleStringFragment
      }
      slug {
        current
      }
      hero {
        ...Hero
      }
      addCloudsToTheFront
      modules {
        ...Modules
      }
      seo {
        ...SEO
      }
    }
  }
`;

const groqQuery = groq`{
  "sanityPage": *[_id == $id][0] {
     ...
  }
}`;

export type BackgroundColorTheme = 'panoramic' | 'evening' | 'sunset' | 'moonlit' | 'starlit';

interface LocalizedQueryData {
  sanityPage: {
    title: string;
    slug: {
      current: string;
    };
    hero?: HeroType;
    addCloudsToTheFront?: boolean;
    modules?: Array<Module>;
    seo?: LocalizedSEO;
  };
}

const Page = withPagePreview(
  {
    groqQuery,
  },
  ({ data }): React.ReactElement => {
    const { i18n } = useTranslation();

    const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
    const localizedData: LocalizedQueryData = localize(data, langs);
    const { slug, title, hero, addCloudsToTheFront, modules, seo } = localizedData.sanityPage;

    function isThemeModule(
      module: Module,
    ): module is ThemeModuleProps & CommonModuleProps & { __typename: 'SanityThemeModule' } {
      return module.__typename === 'SanityThemeModule';
    }

    const pageThemes = modules && modules.length > 0 && modules.filter(isThemeModule);

    const lastThemeOfThePage = (pageThemes &&
      pageThemes.length > 0 &&
      pageThemes[pageThemes.length - 1]?.theme) as BackgroundColorTheme;

    const pageTheme =
      (slug.current !== '/' &&
        modules &&
        modules.length > 0 &&
        isThemeModule(modules[0]) &&
        modules[0].theme) ||
      'panoramic';

    const [homepageHeroRef, homepageHeroIntersectionObserverEntry] = useIntersectionObserver({
      threshold: 0.5,
    });

    return (
      <SanityLayout
        pageTheme={pageTheme}
        lastModuleTheme={lastThemeOfThePage || pageTheme}
        frontCloudPosition={addCloudsToTheFront ? 'fullScreen' : undefined}
        hideCookieBar={
          (homepageHeroIntersectionObserverEntry?.intersectionRatio ??
            (slug.current === '/' ? 1 : 0)) > 0.5
        }
      >
        <PageSEO defaultTitle={slug.current === '/' ? '' : title} pageSEO={seo} />
        {slug.current === '/' ? (
          <div ref={homepageHeroRef}>
            <HomepageHero title={hero?.title || title} />
          </div>
        ) : (
          <Hero {...hero} title={hero?.title || title} />
        )}
        {modules && modules.length > 0 && (
          <ModulesContent modules={modules} pageTheme={pageTheme || 'panoramic'} />
        )}
      </SanityLayout>
    );
  },
);

export default withI18n(Page);
